import { create } from "zustand";

/**
 * 운명 카드 친밀도 — Umamusume 서포트카드 메커니즘 클라이언트 모의.
 *
 * 백엔드에 `character_run_destiny_state.affinity` 컬럼이 도입되기 전까지 localStorage 로 유지.
 * (`rpg.affinity`, JSON: { [runId]: { [destinyId]: 0..4 } })
 *
 * 친밀도 5단계 (0..4). 매주 해당 운명이 파견된 액션을 수행하면 +1. 최대 4.
 * 4 도달 시 향후 서포트 이벤트 트리거 가능 (추후 backend SOUL_WHISPER 연동).
 */

export const AFFINITY_MAX = 4;
const STORAGE_KEY = "rpg.affinity";

type AffinityByDestiny = Record<string, number>;
type AffinityByRun = Record<string, AffinityByDestiny>;

interface AffinityState {
    byRun: AffinityByRun;
}

interface AffinityActions {
    bootstrap: () => void;
    getFor: (runId: string, destinyId: string) => number;
    bumpMany: (runId: string, destinyIds: string[]) => void;
    resetRun: (runId: string) => void;
}

function loadFromStorage(): AffinityByRun {
    if (typeof window === "undefined") return {};
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) as AffinityByRun : {};
    } catch {
        return {};
    }
}

function persistToStorage(byRun: AffinityByRun) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(byRun));
    } catch { /* quota */ }
}

export const useAffinity = create<AffinityState & AffinityActions>((set, get) => ({
    byRun: {},

    bootstrap: () => {
        set({ byRun: loadFromStorage() });
    },

    getFor: (runId, destinyId) => {
        return get().byRun[runId]?.[destinyId] ?? 0;
    },

    bumpMany: (runId, destinyIds) => {
        if (destinyIds.length === 0) return;
        const current = get().byRun;
        const forRun = { ...(current[runId] ?? {}) };
        for (const id of destinyIds) {
            const next = Math.min(AFFINITY_MAX, (forRun[id] ?? 0) + 1);
            forRun[id] = next;
        }
        const nextState = { ...current, [runId]: forRun };
        set({ byRun: nextState });
        persistToStorage(nextState);
    },

    resetRun: (runId) => {
        const current = get().byRun;
        if (!(runId in current)) return;
        const next = { ...current };
        delete next[runId];
        set({ byRun: next });
        persistToStorage(next);
    },
}));
